import {
  CallEnd as CallEndIcon,
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  Group as GroupIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  PictureInPictureAlt as PipIcon,
  Refresh as RefreshIcon,
  ScreenShare as ScreenShareIcon,
  StopScreenShare as StopScreenShareIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  VolumeOff as MuteAllIcon,
} from "@mui/icons-material";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { useState } from "react";

interface ControlBarProps {
  audioMuted: boolean;
  audioStarted: boolean;
  videoOn: boolean;
  isSharing: boolean;
  isHost: boolean;
  pendingKnocks: number;
  /** Lazy URL minter — invoked on each click so a fresh Guest N can be
   *  baked into every copied link. Returns null/undefined if a link can't
   *  be produced yet (e.g. before the room is connected). */
  onCopyLink?: () => string | null | undefined;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleShare: () => void;
  onLeave: () => void;
  onRejoin: () => void;
  /** Omit to hide the participants button (e.g. in mini-mode where the
   *  side panel can't be rendered). */
  onTogglePanel?: () => void;
  onMuteAll?: () => void;
  onPip?: () => void;
}

export default function ControlBar(props: ControlBarProps) {
  const {
    audioMuted,
    audioStarted,
    videoOn,
    isSharing,
    isHost,
    pendingKnocks,
    onCopyLink,
    onToggleAudio,
    onToggleVideo,
    onToggleShare,
    onLeave,
    onRejoin,
    onTogglePanel,
    onMuteAll,
    onPip,
  } = props;
  const micActive = audioStarted && !audioMuted;

  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const url = onCopyLink?.();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked in insecure contexts */
    }
  };

  return (
    <Stack
      direction="row"
      sx={{
        gap: 1.5,
        justifyContent: "center",
        alignItems: "center",
        py: 1.25,
        px: 2,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Tooltip title={micActive ? "Mute" : "Unmute"}>
        <IconButton
          onClick={onToggleAudio}
          sx={controlSx(micActive ? "primary" : "danger")}
        >
          {micActive ? <MicIcon /> : <MicOffIcon />}
        </IconButton>
      </Tooltip>
      <Tooltip title={videoOn ? "Stop video" : "Start video"}>
        <IconButton
          onClick={onToggleVideo}
          sx={controlSx(videoOn ? "primary" : "danger")}
        >
          {videoOn ? <VideocamIcon /> : <VideocamOffIcon />}
        </IconButton>
      </Tooltip>

      <Tooltip title={isSharing ? "Stop sharing" : "Share screen"}>
        <IconButton
          onClick={onToggleShare}
          sx={controlSx(isSharing ? "primary" : "neutral")}
        >
          {isSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
        </IconButton>
      </Tooltip>

      {onTogglePanel && (
        <Tooltip title="Participants">
          <IconButton
            onClick={onTogglePanel}
            sx={{
              ...controlSx("neutral"),
              position: "relative",
            }}
          >
            <GroupIcon />
            {pendingKnocks > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  minWidth: 16,
                  height: 16,
                  px: "4px",
                  borderRadius: 8,
                  bgcolor: "error.main",
                  color: "error.contrastText",
                  fontSize: 10,
                  lineHeight: "16px",
                  textAlign: "center",
                }}
              >
                {pendingKnocks}
              </Box>
            )}
          </IconButton>
        </Tooltip>
      )}

      {isHost && onCopyLink && (
        <Tooltip title={copied ? "Copied!" : "Copy invite link"}>
          <IconButton onClick={handleCopy} sx={controlSx("neutral")}>
            {copied ? <CheckIcon /> : <ContentCopyIcon />}
          </IconButton>
        </Tooltip>
      )}

      {isHost && onMuteAll && (
        <Tooltip title="Mute all">
          <IconButton onClick={onMuteAll} sx={controlSx("neutral")}>
            <MuteAllIcon />
          </IconButton>
        </Tooltip>
      )}

      {onPip && (
        <Tooltip title="Picture-in-picture">
          <IconButton onClick={onPip} sx={controlSx("neutral")}>
            <PipIcon />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Rejoin">
        <IconButton onClick={onRejoin} sx={controlSx("neutral")}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Leave">
        <IconButton onClick={onLeave} sx={controlSx("danger")}>
          <CallEndIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function controlSx(variant: "primary" | "danger" | "neutral") {
  if (variant === "danger") {
    return {
      bgcolor: "error.main",
      color: "error.contrastText",
      "&:hover": { bgcolor: "error.dark" },
    };
  }
  if (variant === "primary") {
    return {
      bgcolor: "primary.main",
      color: "primary.contrastText",
      "&:hover": { bgcolor: "primary.dark" },
    };
  }
  return {
    bgcolor: "action.hover",
    color: "text.primary",
    "&:hover": { bgcolor: "action.selected" },
  };
}
